import React, { useState, useEffect } from 'react';
import {
  getEvents,
  createEvent,
  getProducts,
  getEventPackingLists,
  createEventPackingList,
  getPackingListPDF,
  updateEventStatus,
} from '../api';

function Events({ user }) {
  const [view, setView] = useState('list'); // list, create, packing
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [packingLists, setPackingLists] = useState([]);

  const [eventForm, setEventForm] = useState({
    name: '',
    location: '',
    start_date: '',
    end_date: '',
    notes: '',
  });

  const [packingForm, setPackingForm] = useState({
    container_type: 'box',
    container_number: '',
  });

  const [items, setItems] = useState([{ product_id: '', quantity: 1 }]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadEvents();
    loadProducts();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await getEvents();
      setEvents(response.data);
    } catch (err) {
      setError('Failed to load events');
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to load products');
    }
  };

  const loadPackingLists = async (eventId) => {
    try {
      const response = await getEventPackingLists(eventId);
      setPackingLists(response.data);
    } catch (err) {
      setError('Failed to load packing lists');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await createEvent(eventForm);
      setMessage('Event created successfully');
      setView('list');
      setEventForm({ name: '', location: '', start_date: '', end_date: '', notes: '' });
      loadEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
    }
  };

  const handleCreatePackingList = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      await createEventPackingList(selectedEvent.id, {
        ...packingForm,
        items: items.map((item) => ({
          product_id: parseInt(item.product_id),
          quantity: parseInt(item.quantity),
        })),
      });

      setMessage('Packing list created successfully');
      setPackingForm({ container_type: 'box', container_number: '' });
      setItems([{ product_id: '', quantity: 1 }]);
      loadPackingLists(selectedEvent.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create packing list');
    }
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const viewEventPacking = async (event) => {
    setSelectedEvent(event);
    setView('packing');
    await loadPackingLists(event.id);
  };

  const downloadPDF = async (eventId, listId, containerNumber) => {
    try {
      const response = await getPackingListPDF(eventId, listId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `packing-list-${containerNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await updateEventStatus(eventId, newStatus);
      setMessage(`Event marked as ${newStatus}`);
      loadEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update event status');
    }
  };

  if (user.role !== 'admin') {
    return (
      <div className="container">
        <div className="alert alert-error">
          Only administrators can manage events.
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Event Management</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {view === 'list' ? (
          <>
            <button
              className="btn btn-primary"
              onClick={() => setView('create')}
              style={{ marginBottom: '20px' }}
            >
              Create New Event
            </button>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Location</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>{event.name}</td>
                      <td>{event.location || '-'}</td>
                      <td>
                        {event.start_date && event.end_date
                          ? `${new Date(event.start_date).toLocaleDateString()} - ${new Date(event.end_date).toLocaleDateString()}`
                          : event.start_date
                          ? new Date(event.start_date).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        <span className={`badge badge-${event.status === 'done' ? 'received' : 'pending'}`}>
                          {event.status || 'pending'}
                        </span>
                      </td>
                      <td>{new Date(event.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            className="btn btn-primary"
                            onClick={() => viewEventPacking(event)}
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            Packing Lists
                          </button>
                          {(event.status || 'pending') === 'pending' ? (
                            <button
                              className="btn btn-success"
                              onClick={() => handleStatusChange(event.id, 'done')}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Mark Done
                            </button>
                          ) : (
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleStatusChange(event.id, 'pending')}
                              style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                            >
                              Reopen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : view === 'create' ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setView('list')}
              style={{ marginBottom: '20px' }}
            >
              Back to List
            </button>

            <form onSubmit={handleCreateEvent}>
              <div className="form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  value={eventForm.name}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={eventForm.location}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, location: e.target.value })
                  }
                />
              </div>

              <div style={{ display: 'flex', gap: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={eventForm.start_date}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, start_date: e.target.value })
                    }
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>End Date</label>
                  <input
                    type="date"
                    value={eventForm.end_date}
                    onChange={(e) =>
                      setEventForm({ ...eventForm, end_date: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={eventForm.notes}
                  onChange={(e) =>
                    setEventForm({ ...eventForm, notes: e.target.value })
                  }
                />
              </div>

              <div className="btn-group">
                <button type="submit" className="btn btn-success">
                  Create Event
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setView('list')}
                >
                  Cancel
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setView('list');
                setSelectedEvent(null);
              }}
              style={{ marginBottom: '20px' }}
            >
              Back to Events
            </button>

            <h3>{selectedEvent.name}</h3>
            {selectedEvent.location && <p>Location: {selectedEvent.location}</p>}
            {selectedEvent.start_date && selectedEvent.end_date ? (
              <p>Date: {new Date(selectedEvent.start_date).toLocaleDateString()} - {new Date(selectedEvent.end_date).toLocaleDateString()}</p>
            ) : selectedEvent.start_date ? (
              <p>Date: {new Date(selectedEvent.start_date).toLocaleDateString()}</p>
            ) : null}

            <div
              style={{
                marginTop: '30px',
                marginBottom: '20px',
                padding: '15px',
                border: '1px solid #ddd',
                borderRadius: '5px',
              }}
            >
              <h4>Create Packing List</h4>
              <form onSubmit={handleCreatePackingList}>
                <div className="form-group">
                  <label>Container Type *</label>
                  <select
                    value={packingForm.container_type}
                    onChange={(e) =>
                      setPackingForm({
                        ...packingForm,
                        container_type: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="box">Box</option>
                    <option value="rack">Rack</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Container Number *</label>
                  <input
                    type="text"
                    value={packingForm.container_number}
                    onChange={(e) =>
                      setPackingForm({
                        ...packingForm,
                        container_number: e.target.value,
                      })
                    }
                    placeholder="e.g., Box-1, Rack-A"
                    required
                  />
                </div>

                <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Items</h4>
                {items.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginBottom: '10px',
                      alignItems: 'flex-end',
                    }}
                  >
                    <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
                      <label>Product *</label>
                      <select
                        value={item.product_id}
                        onChange={(e) =>
                          updateItem(index, 'product_id', e.target.value)
                        }
                        required
                      >
                        <option value="">-- Select --</option>
                        {products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} (Stock: {product.quantity})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label>Qty *</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          updateItem(index, 'quantity', e.target.value)
                        }
                        min="1"
                        required
                      />
                    </div>

                    {items.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => removeItem(index)}
                        style={{ padding: '12px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addItem}
                  style={{ marginBottom: '15px' }}
                >
                  Add Item
                </button>

                <div className="btn-group">
                  <button type="submit" className="btn btn-success">
                    Create Packing List
                  </button>
                </div>
              </form>
            </div>

            <h3 style={{ marginTop: '30px' }}>Existing Packing Lists</h3>
            {packingLists.length === 0 ? (
              <p style={{ color: '#7f8c8d' }}>No packing lists yet</p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Number</th>
                      <th>Items</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packingLists.map((list) => (
                      <tr key={list.id}>
                        <td style={{ textTransform: 'capitalize' }}>
                          {list.container_type}
                        </td>
                        <td>{list.container_number}</td>
                        <td>{list.items.length} items</td>
                        <td>{new Date(list.created_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-secondary"
                            onClick={() =>
                              downloadPDF(
                                selectedEvent.id,
                                list.id,
                                list.container_number
                              )
                            }
                            style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                          >
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Events;
